import { createSignal, Show } from 'solid-js'
import './Modal.css'
import { toastStore, userStore } from '/web/store'
import LoginFormSocialButtons from './LoginFromSocialButtons'
import { usersApi } from '/web/store/data/user'
import { SetStoreFunction } from 'solid-js/store'

interface Props {
  onClickLogin?: any
  onClickRegister?: any
  setStore: SetStoreFunction<{
    username: string
    password: string
  }>
}

const ENABLE_SOCIAL_LOGIN = true

const LoginFormModal = (props: Props) => {
  const user = userStore()

  const [showPassword, setShowPassword] = createSignal(false)
  const [showForgotPassword, setShowForgotPassword] = createSignal(false)

  const togglePasswordVisibile = () => {
    setShowPassword(!showPassword())
  }

  const handleForgotPassword = async (e: SubmitEvent) => {
    e.preventDefault()
    const form = e.target as HTMLFormElement
    const email = form.email.value
    try {
      const res = await usersApi.forgotPassword(email)

      if (res.error) {
        toastStore.error(`${res.error}`)
        return
      }

      toastStore.success(
        'If an account exists with that email, you will receive reset instructions shortly'
      )

      // Reset form and return to login view
      form.reset()
      setShowForgotPassword(false)
    } catch (error) {
      toastStore.error(`Error sending forgot password email: ${error}`)
    }
  }

  return (
    <Show when={user.showLogin}>
      <div
        class="fixed bottom-0  left-0 right-0 top-0  z-[200] flex h-screen items-center justify-center overflow-y-auto overflow-x-hidden bg-black/80 backdrop-blur"
        onClick={(e) => {
          userStore.loginModal(false)
          setShowForgotPassword(false)
        }}
      >
        <div
          onClick={(e) => {
            e.stopPropagation()
          }}
          class="background m-5 w-full rounded-2xl border-[1px] border-white p-[18px] backdrop-blur md:w-[400px]"
        >
          <div class="font-clash">
            <Show
              when={!showForgotPassword()}
              fallback={
                <>
                  <h3 class="mb-5 mt-1 font-clash text-2xl font-bold leading-normal text-white">
                    Forgot Password
                  </h3>
                  <form onSubmit={handleForgotPassword}>
                    <div class="mb-6 flex flex-col">
                      <label
                        for="email"
                        class="mb-4 block font-clash text-base font-semibold text-white"
                      >
                        Email Address
                      </label>
                      <input
                        name="email"
                        type="email"
                        placeholder="Enter your email"
                        pattern="[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}"
                        title="Please enter a valid email address"
                        class="rounded-md border border-white bg-white/10 p-4 text-xs"
                        required
                      />
                    </div>
                    <button
                      type="submit"
                      class="mb-3 mt-5 h-12 w-full rounded-md bg-gradient-to-r from-cosplay-blue-200 to-cosplay-purple font-clash font-semibold text-white"
                    >
                      Reset Password
                    </button>
                    <p class="mt-1 text-center text-sm text-white">
                      Remember your password?&nbsp;&nbsp;
                      <a
                        href="#"
                        onClick={(e) => {
                          e.preventDefault()
                          setShowForgotPassword(false)
                        }}
                        class="font-clash font-semibold text-[#FF23FF]"
                      >
                        Log In
                      </a>
                    </p>
                  </form>
                </>
              }
            >
              <h3 class="mb-3 mt-1 font-clash text-2xl font-bold leading-normal text-white">
                User Log In
              </h3>
              <LoginFormSocialButtons show={ENABLE_SOCIAL_LOGIN} user={user} />
              <form
                onSubmit={(e) => {
                  e.preventDefault()
                  props.onClickLogin()
                }}
              >
                <div class="mb-6 flex flex-col">
                  <label
                    for="username"
                    class="mb-3 block font-clash text-base font-semibold text-white"
                  >
                    Username Or Email
                  </label>
                  <input
                    name="username"
                    placeholder="Enter Username Or Email"
                    class="rounded-md border border-white bg-white/10 p-4 text-xs"
                    onInput={(ev) => props.setStore('username', ev.currentTarget.value)}
                  />
                </div>
                <div class="mb-4 flex flex-col">
                  <label
                    for="password"
                    class="mb-3 block font-clash text-base font-semibold text-white"
                  >
                    Password
                  </label>
                  <div class="relative flex">
                    <input
                      name="password"
                      type={showPassword() ? 'text' : 'password'}
                      placeholder="Enter Password"
                      class="flex-1 rounded-md border border-white bg-white/10 p-4 text-xs"
                      onInput={(ev) => props.setStore('password', ev.currentTarget.value)}
                      onKeyDown={(ev) => {
                        if (ev.key === 'Enter') {
                          ev.preventDefault()
                          props.onClickLogin()
                        }
                      }}
                    />
                    <div class="absolute inset-y-0 right-4 flex items-center justify-center">
                      <button type="button" onClick={() => togglePasswordVisibile()}>
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="14"
                          height="14"
                          viewBox="0 0 14 14"
                          fill="none"
                        >
                          <path
                            d="M1.91172 8.92527C1.41589 8.28127 1.16797 7.95869 1.16797 7.0026C1.16797 6.04594 1.41589 5.72452 1.91172 5.07994C2.90164 3.79427 4.5618 2.33594 7.0013 2.33594C9.4408 2.33594 11.101 3.79427 12.0909 5.07994C12.5867 5.7251 12.8346 6.04652 12.8346 7.0026C12.8346 7.95927 12.5867 8.28069 12.0909 8.92527C11.101 10.2109 9.4408 11.6693 7.0013 11.6693C4.5618 11.6693 2.90164 10.2109 1.91172 8.92527Z"
                            stroke="#10E0F9"
                            stroke-width="1.5"
                          />
                          <path
                            d="M8.75 7C8.75 7.46413 8.56563 7.90925 8.23744 8.23744C7.90925 8.56563 7.46413 8.75 7 8.75C6.53587 8.75 6.09075 8.56563 5.76256 8.23744C5.43437 7.90925 5.25 7.46413 5.25 7C5.25 6.53587 5.43437 6.09075 5.76256 5.76256C6.09075 5.43437 6.53587 5.25 7 5.25C7.46413 5.25 7.90925 5.43437 8.23744 5.76256C8.56563 6.09075 8.75 6.53587 8.75 7Z"
                            stroke="#10E0F9"
                            stroke-width="1.5"
                          />
                        </svg>
                      </button>
                    </div>
                  </div>
                </div>
                <div class="flex items-center justify-between ">
                  <div class="flex items-center justify-center gap-1">
                    <input
                      id="terms"
                      type="checkbox"
                      checked
                      class="focus:ring-3 focus:ring-primary-300 dark:focus:ring-primary-600 h-4 w-4 rounded border border-gray-300 bg-gray-50 dark:border-gray-600 dark:bg-gray-700 dark:ring-offset-gray-800"
                    />

                    <label for="terms" class="block text-xs leading-6 text-white">
                      Stay Logged In
                    </label>
                  </div>
                  <a
                    class="text-xs text-[#10E0F9]"
                    href="#"
                    onClick={(e) => {
                      e.preventDefault()
                      setShowForgotPassword(true)
                    }}
                  >
                    Forgot Password?
                  </a>
                </div>
                <button
                  type="submit"
                  class="mb-3 mt-5 h-12 w-full rounded-md bg-gradient-to-r from-cosplay-blue-200 to-cosplay-purple font-clash font-semibold text-white"
                >
                  Log In
                </button>
                <p class="mt-1 text-center text-sm text-white">
                  Don't Have An Account?&nbsp;&nbsp;
                  <a
                    onclick={(e) => {
                      props.onClickRegister(e)
                      setShowForgotPassword(false)
                    }}
                    href="#"
                    class="font-clash font-semibold text-[#FF23FF]"
                  >
                    Sign Up
                  </a>
                </p>
              </form>
            </Show>
          </div>
        </div>
      </div>
    </Show>
  )
}

export default LoginFormModal
